import { _2dMapCombinedQuote } from '@larki/shared/type/order.v2';
import { JobT } from '@larki/shared/types/job.type';
import classNames from 'classnames';
import { upperCase } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import List from '../../../components/list/List';
import { softwares } from '../../../components/mapView/constants';
import Activity from '../../../components/timeline/Activity';
import text, { formatCurrencyForQuote } from '../../../text';
import Section from './Section';
import {
  getCreatorAsMember,
  redistributeOverriddenItems,
  splitQuoteDataIntoCards,
} from './utils';
import useUser from '@/mp/hooks/useUser';
import { UserT } from '@larki/shared/types/user.type';
import { useOptionalStaffPriceEditContext } from '@/mp/components/Cart/StaffPriceEditContext';
import RfqPriceEditBanner from './RfqPriceEditBanner';

const RequestForQuote = ({
  title,
  quote,
  requestForQuoteId,
  job,
  layoutSwitch = { isSoftwareHidden: false },
  createdUser,
}: {
  title: string;
  quote: _2dMapCombinedQuote;
  requestForQuoteId?: number;
  job: JobT;
  layoutSwitch?: { isSoftwareHidden?: boolean };
  createdUser: UserT;
}) => {
  const currentUser = useUser();
  const software = job.customer_software
    ? softwares.entities[
        job.customer_software as keyof typeof softwares.entities
      ]?.name
    : null;

  const { buyNowQuote, getQuoteQuote, buyNowCustomOrders, getQuoteCustomOrders } =
    redistributeOverriddenItems(quote);
  const buyNowCards = splitQuoteDataIntoCards(buyNowQuote);
  const getQuoteCards = splitQuoteDataIntoCards(getQuoteQuote, buyNowCards.length);
  const hasBuyNowCustomOrders = buyNowCustomOrders.length > 0;
  const hasGetQuoteCustomOrders = getQuoteCustomOrders.length > 0;
  const hasBuyNowSection = buyNowCards.length > 0 || hasBuyNowCustomOrders;
  const hasGetQuoteSection = getQuoteCards.length > 0 || hasGetQuoteCustomOrders;

  const quoteHasMembmership = buyNowCards.some(
    (card) => card.title === 'Membership'
  );

  const isEssentialUser =
    quoteHasMembmership || getCreatorAsMember(createdUser, currentUser);

  const buyNowTotals = buyNowQuote.totals;
  const getQuoteTotals = getQuoteQuote.totals;

  const editCtx = useOptionalStaffPriceEditContext();

  return (
    <div className='InvoiceActivity'>
      {editCtx && job?.id != null ? (
        <RfqPriceEditBanner
          jobId={job.id}
          quote={quote}
          requestForQuoteId={requestForQuoteId}
        />
      ) : null}
      <div className='mb-2 font-weight--600 font-18'>
        <Activity.Card.Type type={title} />
      </div>

      {hasBuyNowSection && (
        <>
          <hr className='totals-divider mt-0' />
          <Section
            cards={buyNowCards}
            totals={buyNowTotals}
            quote={quote}
            isEssentialUser={isEssentialUser}
            title={text('buyItems', {
              title: upperCase(text('buyNow')),
            })}
            totalsPrefix={hasGetQuoteSection ? upperCase(text('buyNow')) : ''}
            customOrders={hasBuyNowCustomOrders ? buyNowCustomOrders : undefined}
          />
        </>
      )}

      {hasGetQuoteSection && (
        <>
          <hr
            className={classNames('totals-divider', {
              'mt-4 mb-3': hasBuyNowSection,
              'mt-0': !hasBuyNowSection,
            })}
          />
          <Section
            cards={getQuoteCards}
            totals={getQuoteTotals}
            quote={quote}
            isEssentialUser={isEssentialUser}
            title={text('buyItems', {
              title: upperCase(text('getQuote')),
            })}
            totalsPrefix={hasBuyNowSection ? upperCase(text('getQuote')) : ''}
            isApproximate
            customOrders={
              hasGetQuoteCustomOrders ? getQuoteCustomOrders : undefined
            }
          />
        </>
      )}

      {hasBuyNowSection && hasGetQuoteSection && (
        <>
          <hr className='totals-divider mt-4 mb-3' />
          <Row>
            <Col lg={12} xl={6} className='pr-xl-0'>
              <List isPaddingless className='InvoiceTotal pl-3'>
                {/* @ts-expect-error List.Item not typed */}
                <List.Item
                  description={text('subtotal')}
                  value={formatCurrencyForQuote(
                    quote.totals.totalGross,
                    quote.currency.name,
                    quote.currency.scale_factor,
                    { spaced: true, approximate: true }
                  )}
                  key='invoice-subtotal'
                />

                {/* @ts-expect-error List.Item not typed */}
                <List.Item
                  description={text('gst')}
                  value={formatCurrencyForQuote(
                    quote.totals.taxesTotal,
                    quote.currency.name,
                    quote.currency.scale_factor,
                    { spaced: true, approximate: true }
                  )}
                  key='invoice-gst'
                />

                {/* @ts-expect-error List.Item not typed */}
                <List.Item
                  isHighlighted
                  description={text('total')}
                  key='invoice-total'
                  value={formatCurrencyForQuote(
                    quote.totals.totalNet,
                    quote.currency.name,
                    quote.currency.scale_factor,
                    { spaced: true, approximate: true }
                  )}
                />
              </List>
            </Col>
          </Row>
        </>
      )}

      <p className='pt-1 mt-4 mb-3 grey-3'>{text('rfqNote')}</p>

      {!layoutSwitch.isSoftwareHidden && (
        <div className='mb-3 mt-2'>
          <span className='font-weight-bold'>{text('software')}: </span>
          <span>{software} </span>
        </div>
      )}

      <div className='terms-and-conditions'>
        {text('orderSubjectAgreement', {
          link: (
            <Link
              key='urlTerms'
              to={{ pathname: process.env.LARKI_URL_TERMS }}
              target='_blank'
            >
              Customer {text('termsAndConditions')}
            </Link>
          ),
        })}
      </div>
    </div>
  );
};

export default RequestForQuote;
